import { useState, useEffect, useCallback } from 'react'

interface BeforeInstallPromptEvent extends Event {
    readonly platforms: string[]
    readonly userChoice: Promise<{
        outcome: 'accepted' | 'dismissed'
        platform: string
    }>
    prompt(): Promise<void>
}

/**
 * Kill-switch for the PWA install banner.
 * Keep enabled for the notification activation flow unless the install
 * prompt itself is causing production issues.
 */
const PWA_INSTALL_DISABLED = false

export function usePwaInstall() {
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
    const [isInstallable, setIsInstallable] = useState(false)
    const [isStandalone, setIsStandalone] = useState(false)
    const [isIOS, setIsIOS] = useState(false)

    useEffect(() => {
        // When disabled, skip all detection logic
        if (PWA_INSTALL_DISABLED) return

        // Check for iOS
        const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream
        setIsIOS(isIOSDevice)

        // Check if already in standalone mode
        const checkStandalone = () => {
            const standalone = window.matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone === true
            setIsStandalone(standalone)
            return standalone
        }

        const standalone = checkStandalone()

        if (standalone) {
            setIsInstallable(false)
            return
        }

        const handleBeforeInstallPrompt = (e: Event) => {
            // Prevent Chrome 67 and earlier from automatically showing the prompt
            e.preventDefault()
            // Stash the event so it can be triggered later.
            setDeferredPrompt(e as BeforeInstallPromptEvent)
            setIsInstallable(true)
        }

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)

        // On iOS, we show a custom guide since there is no 'beforeinstallprompt'
        if (isIOSDevice && !standalone) {
            setIsInstallable(true)
        }

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
        }
    }, [])

    const promptInstall = useCallback(async () => {
        if (PWA_INSTALL_DISABLED || !deferredPrompt) {
            return
        }
        // Show the prompt
        await deferredPrompt.prompt()
        // Wait for the user to respond to the prompt
        const { outcome } = await deferredPrompt.userChoice
        console.log(`User response to the install prompt: ${outcome}`)
        // We've used the prompt, and can't use it again, discard it
        setDeferredPrompt(null)
        setIsInstallable(false)
    }, [deferredPrompt])

    // When disabled, always report as not installable
    if (PWA_INSTALL_DISABLED) {
        return {
            isInstallable: false,
            isStandalone: false,
            isIOS: false,
            promptInstall,
        }
    }

    return {
        isInstallable,
        isStandalone,
        isIOS,
        promptInstall
    }
}
